/**
 * Detects if the current device is iOS (iPhone, iPad, iPod).
 * @returns true if iOS, false otherwise
 */
export function isIOS(): boolean {
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
}

/**
 * Detects if the current device is Android.
 * @returns true if Android, false otherwise
 */
export function isAndroid(): boolean {
  return /android/i.test(navigator.userAgent);
}

/**
 * Shares squad information using native share functionality on mobile or email on desktop.
 * @param squadCode The squad code to share
 */
export async function shareSquad(squadCode: string): Promise<void> {
  const appName = "Extraction Zero";
  const message = `Join my ${appName} squad! Squad code: ${squadCode}`;

  // App store and Google Play URLs
  const appStoreUrl = "https://apps.apple.com/app/extraction-zero/id123456789"; // TODO: Update with actual App Store ID
  const googlePlayUrl =
    "https://play.google.com/store/apps/details?id=com.bubblesquid.extractionzero";
  const websiteUrl = "https://www.extractionzero.com";

  // For iOS
  if (isIOS()) {
    try {
      // Use native Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: appName,
          text: message,
          url: appStoreUrl,
        });
      } else {
        // Fallback: Open email client with share info
        openEmailShare(message, appStoreUrl);
      }
    } catch (error) {
      console.error("Error sharing on iOS:", error);
      openEmailShare(message, appStoreUrl);
    }
  }
  // For Android
  else if (isAndroid()) {
    try {
      // Use native Web Share API if available
      if (navigator.share) {
        await navigator.share({
          title: appName,
          text: message,
          url: googlePlayUrl,
        });
      } else {
        // Fallback: Open email client with share info
        openEmailShare(message, googlePlayUrl);
      }
    } catch (error) {
      console.error("Error sharing on Android:", error);
      openEmailShare(message, googlePlayUrl);
    }
  }
  // For desktop
  else {
    openEmailShare(message, websiteUrl);
  }
}

/**
 * Opens the default email client with squad sharing information.
 * @param message The message to share
 * @param appUrl The app store or website URL
 */
function openEmailShare(message: string, appUrl: string): void {
  const subject = encodeURIComponent("Join my Extraction Zero Squad!");
  const body = encodeURIComponent(`${message}\nPlay the game: ${appUrl}`);
  const mailtoLink = `mailto:?subject=${subject}&body=${body}`;
  window.location.href = mailtoLink;
}
